/**
 * Course and faculty fields arrive from the Services API as plain
 * comma-separated strings (e.g. "STU-1313", "Celizna, Bagnole, Kloepfer"),
 * already display-ready. These helpers clean and combine them.
 */

/**
 * The course-number fields shown on calendar rows, grouped as the retired
 * system grouped them: values within a group join with ", ", groups join
 * with a space. Faculty renders last. `review_course_affil` ("Option
 * Studio") is deliberately excluded — it appears only in the detail modal
 * and review emails.
 */
const COURSE_FACULTY_FIELD_GROUPS = [
  ['core_stu_affiliation'],
  ['opt_stu_affiliation', 'opt_stu_affil_fall'],
  ['adv_affiliation'],
  ['faculty_affiliation'],
];

/**
 * One-line course + faculty indicator for an event, e.g. "STU-1313 Cho".
 * Empty when the event carries no course data (in practice, anything that
 * is not a review).
 */
export function eventCourseFacultyLine(event) {
  return COURSE_FACULTY_FIELD_GROUPS.map(fields =>
    uniqueTextValues(fields.flatMap(field => textValues(event?.[field]))).join(', ')
  )
    .filter(Boolean)
    .join(' ');
}

/**
 * Structured affiliation groups from the presenter in display order,
 * the label is null when the parent is a suppressed SERT placeholder.
 */
export function eventAffiliationGroups(event) {
  return Array.isArray(event?.affiliations) ? event.affiliations : [];
}

/**
 * Normalize an affiliation field into displayable strings: unwraps arrays
 * and JSON-encoded values, trims, and drops empty / "null" /
 * "not applicable" placeholders.
 */
export function textValues(value) {
  if (Array.isArray(value)) {
    return value.flatMap(item => textValues(item));
  }

  if (value === null || value === undefined) {
    return [];
  }

  const text = String(value).trim();

  if (!text || text.toLowerCase() === 'null') {
    return [];
  }

  const parsed = parseJsonValue(text);

  if (parsed !== null && parsed !== text) {
    return Array.isArray(parsed) || typeof parsed === 'string' ? textValues(parsed) : [];
  }

  return /not\s+applicable/i.test(text) ? [] : [text];
}

export function uniqueTextValues(values) {
  return values.filter((value, index, list) => list.indexOf(value) === index);
}

function parseJsonValue(value) {
  if (!/^(?:\[|{|")/.test(value)) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
